import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { weekIsFull } from "../lib/planner";
import type {
  Movie,
  MovieDetail,
  Showtime,
  TheaterShowtimes,
  WeekUsage,
} from "../lib/types";

// One movie, however it was found. Extracted from `pages/Showtimes.tsx` when
// Browse, Search and Daily became three views of the same data: a film has to
// look identical whichever one surfaced it, and three copies of this card is
// exactly the shape that drifts until it does not.

function MoviePoster({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded bg-neutral-800 text-3xl">
        🎬
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      // onError, not a broken-image icon: Fandango poster URLs expire, and a
      // dead <img> renders as a torn-page glyph that looks like an app bug.
      onError={() => setFailed(true)}
      className="h-full w-full rounded object-cover"
    />
  );
}

/** One card's open panel: which peek is showing, and how a control changes it.
 *
 *  Keys are `verdict:critics`, `verdict:audience` and `person:<name>`. A click
 *  pins one open; a mouse hover shows one only while it lasts, and a pinned one
 *  wins, so moving the mouse across other controls does not swap it out. One
 *  key per card means one panel at a time. Each control hands over its own
 *  element, which is what a popover is anchored to.
 */
interface Peek {
  open: string | null;
  hover: (key: string | null, anchor?: HTMLElement) => void;
  toggle: (key: string, anchor: HTMLElement) => void;
}

/** A word on the card that opens a panel of more about it.
 *
 *  A mouse opens it on hover and a click pins it; a touch screen has no hover,
 *  so a tap toggles it. Pointer events tell the two apart: the emulated
 *  mouseenter a tap fires would otherwise open the panel and the click right
 *  behind it would close it again.
 */
function PeekButton({
  peekKey,
  peek,
  label,
  children,
}: {
  peekKey: string;
  peek: Peek;
  label: string;
  children: React.ReactNode;
}) {
  const open = peek.open === peekKey;
  return (
    <button
      type="button"
      data-peek=""
      aria-expanded={open}
      aria-label={label}
      onPointerEnter={(e) => e.pointerType === "mouse" && peek.hover(peekKey, e.currentTarget)}
      onPointerLeave={(e) => e.pointerType === "mouse" && peek.hover(null)}
      onClick={(e) => peek.toggle(peekKey, e.currentTarget)}
      // common-web gives every button a 44px min-height. Inline, that tap area
      // reached over the neighbouring lines and took their clicks (a press on
      // the director landed on the cast name below), so it is cancelled here
      // and the name lines get taller line-height instead.
      className={`-my-[2px] min-h-0! cursor-pointer py-[2px] underline decoration-dotted underline-offset-2 hover:text-neutral-200 ${
        open ? "text-neutral-200 decoration-neutral-300" : "decoration-neutral-600"
      }`}
    >
      {children}
    </button>
  );
}

/** Below Tailwind's `sm`: the panel becomes a sheet from the bottom of the
 *  screen, since a popover beside a name on a phone covers the card it explains. */
const PHONE_QUERY = "(max-width: 639px)";

function isPhone() {
  return typeof window !== "undefined" && !!window.matchMedia?.(PHONE_QUERY).matches;
}

/** What a peek opened.
 *
 *  Desktop: a popover under the control it came from, floating over the card so
 *  nothing below it moves, clamped to the card's width. Phone: a sheet along the
 *  bottom of the screen. Its dimmed backdrop takes no taps, so a tap on another
 *  name or score behind it opens that one directly; the card closes the sheet on
 *  any other tap (see `MovieCard`).
 */
function PeekPanel({
  label,
  anchor,
  container,
  onClose,
  onPointerEnter,
  onPointerLeave,
  children,
}: {
  label: string;
  anchor: HTMLElement | null;
  container: HTMLElement | null;
  onClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  children: (size: "popover" | "sheet") => React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top: number; left: number; arrow: number } | null>(null);
  const phone = isPhone();

  useLayoutEffect(() => {
    const panel = ref.current;
    if (phone || !panel || !anchor || !container) return;
    const box = container.getBoundingClientRect();
    const at = anchor.getBoundingClientRect();
    const width = panel.offsetWidth;
    const left = Math.max(8, Math.min(at.left - box.left - 12, box.width - width - 8));
    setPlace({
      top: at.bottom - box.top + 8,
      left,
      arrow: Math.max(12, Math.min(width - 22, at.left - box.left - left + at.width / 2 - 5)),
    });
  }, [phone, anchor, container, label]);

  if (phone) {
    return createPortal(
      <>
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-40 bg-black/50" />
        <div
          ref={ref}
          role="dialog"
          aria-label={label}
          data-peek-panel=""
          className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-neutral-700 bg-neutral-900 px-4 pt-2 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-[0_-12px_32px_rgba(0,0,0,0.55)]"
        >
          <span aria-hidden="true" className="mx-auto block h-1 w-9 rounded-full bg-neutral-700" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute top-1 right-2 px-3 text-lg text-neutral-500 hover:text-neutral-300"
          >
            ×
          </button>
          <div className="pt-3">{children("sheet")}</div>
        </div>
      </>,
      document.body,
    );
  }
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      data-peek-panel=""
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={place ? { top: place.top, left: place.left } : { top: 0, left: 0, visibility: "hidden" }}
      className="absolute z-30 w-80 max-w-[calc(100%-1rem)] rounded-lg border border-neutral-700 bg-neutral-900 p-3 shadow-[0_12px_32px_rgba(0,0,0,0.55)]"
    >
      <span
        aria-hidden="true"
        style={{ left: place?.arrow ?? 12 }}
        className="absolute -top-[6px] h-2.5 w-2.5 rotate-45 border-t border-l border-neutral-700 bg-neutral-900"
      />
      {children("popover")}
    </div>
  );
}

/** A TMDb headshot, or the person's initials when there is none or it fails. */
function Headshot({ name, url, size }: { name: string; url: string | undefined; size: "popover" | "sheet" }) {
  const [failed, setFailed] = useState(false);
  const box = size === "sheet" ? "h-18 w-18" : "h-14 w-14";
  if (!url || failed) {
    const initials = name.split(/\s+/).map((w) => w[0]).join("").slice(0, 3);
    return (
      <span
        aria-hidden="true"
        className={`${box} flex shrink-0 items-center justify-center rounded-full bg-neutral-800 font-semibold text-neutral-400`}
      >
        {initials}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      onError={() => setFailed(true)}
      className={`${box} shrink-0 rounded-full border border-neutral-700 object-cover`}
    />
  );
}

function PersonPeek({
  name,
  titles,
  photo,
  size,
}: {
  name: string;
  titles: MovieDetail["known_for"][string];
  photo: string | undefined;
  size: "popover" | "sheet";
}) {
  return (
    <div className="flex items-start gap-3">
      <Headshot name={name} url={photo} size={size} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-neutral-100">{name}</p>
        <p className="text-[10px] uppercase tracking-wide text-neutral-500">Known for</p>
        <ol className="mt-1 space-y-0.5 text-sm text-neutral-300">
          {titles.map((t) => (
            <li key={t.title}>
              {t.title}
              {t.year && <span className="ml-1.5 tabular-nums text-neutral-500">{t.year}</span>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function VerdictPeek({ score, label, text }: { score: string | null; label: string; text: string }) {
  return (
    <div>
      {score && <p className="text-xl leading-none font-semibold text-neutral-100">{score}</p>}
      <p className="mt-1 text-[10px] uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-1.5 text-sm text-neutral-300">{text}</p>
    </div>
  );
}

type VerdictKey = keyof MovieDetail["verdict"];

/** Which RT verdict a score opens. The critic score speaks for the critics'
 *  consensus and the audience score for "Audience Says"; nothing else has one. */
const VERDICT_FOR_SOURCE: Record<string, VerdictKey> = {
  rotten_tomatoes: "critics",
  rotten_tomatoes_audience: "audience",
};
const VERDICT_LABEL: Record<VerdictKey, string> = {
  critics: "Critics consensus",
  audience: "Audience says",
};

/** RT / IMDb / Metacritic, in the order and grouping the server decided.
 *
 *  Groups are rendered flush and separated by a middot, matching `app.py`'s
 *  ratings line -- the RT critic score and its audience score are one score
 *  read two ways and belong side by side. A score with an RT verdict behind it
 *  opens that verdict.
 */
function RatingsRow({
  ratings,
  verdict,
  peek,
}: {
  ratings: MovieDetail["ratings"];
  verdict?: MovieDetail["verdict"];
  peek?: Peek;
}) {
  const byGroup = new Map<number, MovieDetail["ratings"]>();
  for (const rating of ratings) {
    const existing = byGroup.get(rating.group);
    if (existing) existing.push(rating);
    else byGroup.set(rating.group, [rating]);
  }
  const groups = [...byGroup.entries()].sort(([a], [b]) => a - b).map(([, entries]) => entries);

  return (
    <span className="text-xs text-neutral-400">
      {groups.map((entries, index) => (
        <span key={entries[0].source} className="whitespace-nowrap">
          {index > 0 && <span className="px-1.5 text-neutral-600">·</span>}
          {entries.map((rating) => {
            const key = VERDICT_FOR_SOURCE[rating.source];
            const score = `${rating.emoji} ${rating.display}`;
            return (
              <span key={rating.source} className="mr-1.5 whitespace-nowrap">
                {key && verdict?.[key] && peek ? (
                  <PeekButton
                    peekKey={`verdict:${key}`}
                    peek={peek}
                    label={`${rating.display}: ${VERDICT_LABEL[key]}`}
                  >
                    {score}
                  </PeekButton>
                ) : (
                  score
                )}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

/** What Sarah said about this film, when she said either of the two things
 *  worth acting on.
 *
 *  Only yes and no render. "Maybe later" is her deferring the question, not
 *  answering it, and it is the deck's own rotation mechanism on the other side
 *  -- surfacing it here would put a card on the page whose content is "no
 *  decision yet", which is what an absent line already says more quietly.
 *
 *  `watched` renders nothing either, but it does SUPPRESS the line. She keeps
 *  the yes on a film after they have been to it (sarah-site drops it from her
 *  own list but never rewrites the vote), so without this a film they saw last
 *  week keeps saying "Sarah wants to see this" for as long as it is still in
 *  theaters -- a stale to-do rather than a record.
 */
function SarahAnswer({
  vote,
  watched,
}: {
  vote: Movie["sarah_vote"];
  watched: boolean;
}) {
  if (watched) return null;
  if (vote !== "yes" && vote !== "no") return null;
  const label = {
    yes: { icon: "🍿", text: "Sarah wants to see this", tone: "text-pink-400" },
    no: { icon: "🙅", text: "Sarah passed on this", tone: "text-neutral-500" },
  }[vote];
  return (
    <p className={`text-xs ${label.tone}`}>
      <span aria-hidden="true">{label.icon}</span> {label.text}
    </p>
  );
}

/** A name, opening what that person is best known for when the nightly step
 *  found anything; plain text otherwise. */
function PersonName({
  name,
  detail,
  peek,
}: {
  name: string;
  detail: MovieDetail;
  peek: Peek;
}) {
  if (!detail.known_for[name]) return <>{name}</>;
  return (
    <PeekButton peekKey={`person:${name}`} peek={peek} label={`${name}: known for`}>
      {name}
    </PeekButton>
  );
}

/** Comma-separated names. Each comma is held to the name before it, since
 *  Chrome will otherwise break the line between a button and its comma and
 *  start the next line with ", ". */
function NameList({ names, detail, peek }: { names: string[]; detail: MovieDetail; peek: Peek }) {
  return (
    <>
      {names.map((name, i) => (
        <span key={name}>
          <span className="whitespace-nowrap">
            <PersonName name={name} detail={detail} peek={peek} />
            {i < names.length - 1 && ","}
          </span>
          {i < names.length - 1 && " "}
        </span>
      ))}
    </>
  );
}

/** Synopsis, tagline, director and cast, on the card.
 *
 *  The synopsis is clamped to three lines with a "more" toggle: the page's job
 *  is scanning a date's worth of movies, and forty full synopses on a phone
 *  turn that into scrolling.
 */
function MovieBlurb({ detail, peek }: { detail: MovieDetail; peek: Peek }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Whether three lines actually cut anything off, re-measured as the card's
  // width changes (rotating a phone, resizing a window). Only offered when it
  // does, so a short synopsis carries no "more" that reveals nothing.
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el || expanded) return;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [detail.description, expanded]);
  // Said out loud rather than rendered as an empty slot. A card that found
  // nothing and one still waiting look identical otherwise -- both are a card
  // with no ratings row -- and for AMC-only events (Fathom, Screen Unseen)
  // "nothing found" is the routine, permanent answer, not a pending one.
  if (!detail.available) {
    return <p className="text-xs text-neutral-600">No ratings or details found</p>;
  }

  // `director` is TMDb's directors joined with ", " (the server keeps the string
  // for its own matching), so splitting it back gives each name its own peek.
  const directors = detail.director ? detail.director.split(", ").filter(Boolean) : [];

  // The tagline counts. It is rendered below, so leaving it out of this guard
  // made a tagline-only entry -- a thin TMDb record with no overview, cast or
  // director -- return null and become permanently unreachable.
  if (!detail.description && !detail.tagline && directors.length === 0 && detail.cast.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1 text-sm text-neutral-300">
      {detail.tagline && <p className="italic text-neutral-500">“{detail.tagline}”</p>}
      {detail.description && (
        <div>
          <p ref={descriptionRef} className={expanded ? "" : "line-clamp-3"}>
            {detail.description}
          </p>
          {(clamped || expanded) && (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-neutral-500 hover:text-neutral-300"
            >
              {expanded ? "less" : "more"}
            </button>
          )}
        </div>
      )}
      {directors.length > 0 && (
        <p className="text-xs leading-6 text-neutral-500">
          Directed by <NameList names={directors} detail={detail} peek={peek} />
        </p>
      )}
      {detail.cast.length > 0 && (
        <p className="text-xs leading-6 text-neutral-500">
          <NameList names={detail.cast} detail={detail} peek={peek} />
        </p>
      )}
    </div>
  );
}

/** The trailer, as a poster frame that becomes a player on click.
 *
 *  Not an iframe from the start. A date carries forty cards, and forty YouTube
 *  players is forty third-party frames loading their own scripts before anyone
 *  has asked to watch anything -- which is what the Streamlit page did, one
 *  card at a time, because it had no way to defer. The thumbnail is a single
 *  image request; the player is built only for the one you click.
 *
 *  Rendered outside the details disclosure and outside its `available` guard,
 *  because `available` answers "is there anything to READ" and a film can have
 *  a trailer and no synopsis -- an AMC-only event with a TMDb video is exactly
 *  that shape.
 */
function Trailer({ youtubeId, title }: { youtubeId: string; title: string }) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <iframe
        // autoplay: the click that got here WAS the play button, so requiring a
        // second one inside the frame is the whole point of the pattern lost.
        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1`}
        title={`${title} trailer`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
        allowFullScreen
        className="aspect-video w-full max-w-xs rounded border border-neutral-800"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play the ${title} trailer`}
      className="group/trailer relative block w-full max-w-xs overflow-hidden rounded border border-neutral-800"
    >
      <img
        src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        className="aspect-video w-full object-cover"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-3xl text-white/90 group-hover/trailer:bg-black/10">
        &#9654;
      </span>
    </button>
  );
}

/** The plan control, as the right-hand segment of a showtime chip.
 *
 *  Part of the chip rather than a button beneath it. The Streamlit page drew a
 *  parallel row of `st.columns` buttons under each theater's links because a
 *  button could not live inside a markdown string; the alignment broke past
 *  about six showtimes, and the row cost as much vertical space as the times
 *  themselves.
 */
function PlanSegment({
  planned,
  full,
  busy,
  onClick,
}: {
  planned: boolean;
  full: boolean;
  busy: boolean;
  onClick: () => void;
}) {
  // A full week disables ADDING only. Removing a plan from a full week is the
  // one action that makes it not full, so disabling that would be a dead end.
  const blocked = full && !planned;
  const label = planned ? "Remove from planner" : blocked ? "Week full" : "Add to planner";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || blocked}
      aria-pressed={planned}
      aria-label={label}
      title={label}
      className={`min-w-[1.75rem] border-l px-1.5 text-xs disabled:cursor-not-allowed ${
        planned
          ? "border-emerald-700/60 bg-emerald-900/40 text-emerald-300"
          : blocked
            ? "border-neutral-800 text-neutral-700"
            : "border-neutral-700 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200"
      } ${busy ? "opacity-40" : ""}`}
    >
      {planned ? "\u2713" : blocked ? "\u26a0" : "+"}
    </button>
  );
}

// The url kinds that land somewhere other than this showtime's seat map -- what
// an AMC scrape block leaves us. `theater_page` is the theater's day page (right
// theater, right date); `movie_page` is the film's (right film, right date,
// every theater). Both are worth offering and neither is worth passing off as
// the real thing, so each says where it goes and wears a dotted underline. A
// kind absent here is one click from booking.
const INDIRECT_DESTINATIONS: Record<string, string> = {
  theater_page: "opens this theater's showtimes page",
  movie_page: "opens this film's showtimes for this date",
};

export function ShowtimeChip({
  showtime,
  theater,
  full = false,
  busy = false,
  onPlan,
}: {
  showtime: Showtime;
  theater?: string;
  full?: boolean;
  busy?: boolean;
  onPlan?: (theater: string, showtime: Showtime, planned: boolean) => void;
}) {
  const label = (
    <>
      {showtime.display_time}
      {showtime.format && (
        <span className="ml-1 text-[10px] uppercase tracking-wide text-neutral-400">
          {showtime.format}
        </span>
      )}
      {showtime.icons.length > 0 && <span className="ml-1">{showtime.icons.join("")}</span>}
    </>
  );

  // A past showtime is rendered but never linked -- the API sends no url for
  // one. Keeping it visible is deliberate: "the 11am already went" is useful,
  // and silently dropping it makes the day look emptier than it was. It gets
  // no plan control either: there is nothing left to go to.
  if (showtime.is_past) {
    return (
      <span
        className="rounded border border-neutral-800 px-2 py-1 text-sm text-neutral-600 line-through"
        title="Already started"
      >
        {label}
      </span>
    );
  }

  const indirectDestination =
    INDIRECT_DESTINATIONS[showtime.url_kind ?? ""] ?? null;
  // A slot AMC does not list reads as uncertain for a different reason than an
  // indirect destination, and wears the same dimmed treatment.
  const amcUnlisted = showtime.amc_unlisted_time === true;
  const isIndirect = indirectDestination !== null || amcUnlisted;

  // A future showtime with no bookable link still gets its plan control: the
  // planner stores the theater page's URL, which exists either way, and this
  // is the case where planning it is the only way to keep track of it.
  const time = showtime.url ? (
    <a
      href={showtime.url}
      target="_blank"
      rel="noreferrer"
      title={
        amcUnlisted
          ? "AMC's page does not list this showtime - books on Fandango"
          : showtime.url_kind === "fandango"
            ? "Book this showtime on Fandango"
            : indirectDestination
              ? `Direct showtime link unavailable - ${indirectDestination}`
              : undefined
      }
      className={
        isIndirect
          ? "px-2 py-1 text-sm text-neutral-400 underline decoration-dotted underline-offset-2 hover:bg-neutral-800"
          : "px-2 py-1 text-sm text-neutral-100 hover:bg-neutral-800"
      }
    >
      {label}
    </a>
  ) : (
    <span
      className="px-2 py-1 text-sm text-neutral-600 line-through"
      title="No booking link available"
    >
      {label}
    </span>
  );

  if (!onPlan || !theater) {
    return (
      <span className="inline-flex overflow-hidden rounded border border-neutral-700 bg-neutral-900">
        {time}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-stretch overflow-hidden rounded border bg-neutral-900 ${
        showtime.planned ? "border-emerald-700/60" : "border-neutral-700"
      }`}
    >
      {time}
      <PlanSegment
        planned={showtime.planned}
        full={full}
        busy={busy}
        onClick={() => onPlan(theater, showtime, !showtime.planned)}
      />
    </span>
  );
}

function TheaterRow({
  theater,
  weeks,
  busy,
  onPlan,
}: {
  theater: TheaterShowtimes;
  weeks?: Record<string, WeekUsage>;
  busy?: boolean;
  onPlan?: (theater: string, showtime: Showtime, planned: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] items-baseline gap-x-2">
      <span className="text-xs text-neutral-500">{theater.name}</span>
      <div className="flex min-w-0 flex-wrap gap-1.5">
        {theater.showtimes.map((showtime, index) => (
          <ShowtimeChip
            key={`${showtime.screening_id ?? `${showtime.time}-${showtime.auditorium}-${showtime.format}`}-${index}`}
            showtime={showtime}
            theater={theater.theater}
            full={weeks ? weekIsFull(weeks, showtime) : false}
            busy={busy}
            onPlan={onPlan}
          />
        ))}
      </div>
    </div>
  );
}

function ListToggle({
  on,
  onLabel,
  offLabel,
  onClick,
  busy,
}: {
  on: boolean;
  onLabel: string;
  offLabel: string;
  onClick: () => void;
  busy: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      aria-pressed={on}
      aria-label={on ? onLabel : offLabel}
      title={on ? onLabel : offLabel}
      className={`rounded-full px-2 py-0.5 text-xs disabled:opacity-40 ${
        on
          ? "bg-neutral-700 text-neutral-100"
          : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
      }`}
    >
      {on ? onLabel : offLabel}
    </button>
  );
}

export function MovieCard({
  movie,
  detail,
  weeks,
  onSetList,
  onPlan,
}: {
  movie: Movie;
  detail: MovieDetail | undefined;
  /** A-List usage per week, for deciding which chips can still be planned.
   *  Absent on a card rendered outside a browsing response. */
  weeks?: Record<string, WeekUsage>;
  onSetList: (title: string, list: "watchlist" | "not-interested", on: boolean) => Promise<void>;
  onPlan?: (theater: string, showtime: Showtime, planned: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [planning, setPlanning] = useState(false);

  // Serialized per card rather than per chip. Two plans landing at once would
  // both read the same week usage and both think there was room, and the
  // second would come back 409 after the first had already taken the slot.
  const plan = onPlan
    ? async (theater: string, showtime: Showtime, planned: boolean) => {
        setPlanning(true);
        try {
          await onPlan(theater, showtime, planned);
        } finally {
          setPlanning(false);
        }
      }
    : undefined;

  const meta = [
    movie.runtime ? `${Math.floor(movie.runtime / 60)}h ${movie.runtime % 60}m` : null,
    movie.mpaa_rating,
    movie.genres.slice(0, 2).join(", ") || null,
  ].filter(Boolean);
  const ratings = detail?.ratings ?? [];
  const [pinned, setPinned] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const cardRef = useRef<HTMLElement>(null);
  const hoverEnds = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keepHover = () => {
    if (hoverEnds.current) clearTimeout(hoverEnds.current);
    hoverEnds.current = null;
  };
  // A short grace period, so the mouse can travel from a name onto its popover.
  const endHover = () => {
    keepHover();
    hoverEnds.current = setTimeout(() => setHovered(null), 150);
  };
  const close = () => {
    keepHover();
    setPinned(null);
    setHovered(null);
  };
  const peek: Peek = {
    open: pinned ?? hovered,
    hover: (key, el) => {
      if (key === null) return endHover();
      keepHover();
      if (!pinned && el) setAnchor(el);
      setHovered(key);
    },
    toggle: (key, el) => {
      keepHover();
      setAnchor(el);
      setPinned((cur) => (cur === key ? null : key));
      setHovered(null);
    },
  };
  useEffect(() => () => keepHover(), []);

  // An open panel closes on Escape, or on a press anywhere except the panel
  // itself and this card's own peek controls -- those open their own panel in
  // the same tap, which is what lets a phone's sheet switch without a second.
  const isOpen = peek.open !== null;
  useEffect(() => {
    if (!isOpen) return;
    // `close`, restated from refs and setters so the listeners need no deps.
    const close = () => {
      if (hoverEnds.current) clearTimeout(hoverEnds.current);
      setPinned(null);
      setHovered(null);
    };
    const onPress = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest("[data-peek-panel]")) return;
      if (target?.closest("[data-peek]") && cardRef.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("pointerdown", onPress);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPress);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  const openVerdict = peek.open?.startsWith("verdict:")
    ? (peek.open.slice("verdict:".length) as VerdictKey)
    : null;
  const openVerdictText = openVerdict ? detail?.verdict[openVerdict] : null;
  const openVerdictScore = openVerdict
    ? ratings.find((r) => VERDICT_FOR_SOURCE[r.source] === openVerdict)
    : undefined;
  const openPerson = peek.open?.startsWith("person:") ? peek.open.slice("person:".length) : null;
  const openTitles = openPerson ? detail?.known_for[openPerson] : undefined;
  const panel =
    openVerdict && openVerdictText
      ? {
          label: VERDICT_LABEL[openVerdict],
          body: () => (
            <VerdictPeek
              score={openVerdictScore ? `${openVerdictScore.emoji} ${openVerdictScore.display}` : null}
              label={VERDICT_LABEL[openVerdict]}
              text={openVerdictText}
            />
          ),
        }
      : openPerson && openTitles
        ? {
            label: `${openPerson} is known for`,
            body: (size: "popover" | "sheet") => (
              <PersonPeek
                name={openPerson}
                titles={openTitles}
                photo={detail?.photos[openPerson]}
                size={size}
              />
            ),
          }
        : null;

  const toggle = async (list: "watchlist" | "not-interested", on: boolean) => {
    setBusy(true);
    try {
      await onSetList(movie.title, list, on);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      ref={cardRef}
      className={`relative flex gap-4 rounded-lg ${panel ? "z-10" : ""} border border-neutral-800 bg-neutral-900/50 p-4 ${
        // Dimmed, not hidden. With "hide not interested" off you have asked to
        // see these, and marking one from the list should show it take effect
        // rather than making the row vanish under your finger.
        movie.not_interested ? "opacity-50" : ""
      }`}
    >
      <div className="h-36 w-24 shrink-0">
        {/* TMDb's poster once it arrives, Fandango's until then. The detail
            response sends null when it has nothing better, so this never
            replaces a working poster with a placeholder. */}
        <MoviePoster url={detail?.poster_url ?? movie.poster_url} />
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-tight">{movie.title}</h2>
            {meta.length > 0 && <p className="text-xs text-neutral-400">{meta.join(" · ")}</p>}
          </div>
          <div className="flex shrink-0 gap-1.5">
            <ListToggle
              on={movie.in_watchlist}
              onLabel="★ Watchlist"
              offLabel="☆ Watchlist"
              busy={busy}
              onClick={() => void toggle("watchlist", !movie.in_watchlist)}
            />
            <ListToggle
              on={movie.not_interested}
              onLabel="🚫 Not interested"
              offLabel="🚫"
              busy={busy}
              onClick={() => void toggle("not-interested", !movie.not_interested)}
            />
          </div>
        </div>

        {/* Its own line, under the header rather than beside the list buttons:
            on a phone that column is a few words wide and split the scores. */}
        {ratings.length > 0 && (
          <p className="text-xs leading-6 text-neutral-400">
            <RatingsRow ratings={ratings} verdict={detail?.verdict} peek={peek} />
          </p>
        )}

        <SarahAnswer vote={movie.sarah_vote} watched={movie.sarah_watched} />

        {movie.badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {movie.badges.map((badge) => (
              <span
                key={`${badge.kind}-${badge.flag}-${badge.label}`}
                className="rounded-full bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300"
              >
                {badge.icon} {badge.label}
                {badge.showtime_count !== null && ` (${badge.showtime_count})`}
              </span>
            ))}
          </div>
        )}

        {detail && <MovieBlurb detail={detail} peek={peek} />}
        {detail?.trailer_youtube_id && (
          <Trailer youtubeId={detail.trailer_youtube_id} title={movie.title} />
        )}

        {/* Two shapes, and the server picks which one by whether the request
            covered one date or a range. `dates` is not a superset of
            `theaters` -- reading whichever happens to be present is what keeps
            a single date from growing a redundant date heading on every card. */}
        <div className="space-y-1.5">
          {movie.dates
            ? movie.dates.map((group) => (
                <div key={group.date} className="space-y-1">
                  <p className="text-xs font-medium text-neutral-400">{group.date_label}</p>
                  {group.theaters.map((theater) => (
                    <TheaterRow
                      key={theater.theater}
                      theater={theater}
                      weeks={weeks}
                      busy={planning}
                      onPlan={plan ? (t, s, p) => void plan(t, s, p) : undefined}
                    />
                  ))}
                </div>
              ))
            : (movie.theaters ?? []).map((theater) => (
                <TheaterRow
                  key={theater.theater}
                  theater={theater}
                  weeks={weeks}
                  busy={planning}
                  onPlan={plan ? (t, s, p) => void plan(t, s, p) : undefined}
                />
              ))}
        </div>
      </div>
      {panel && (
        <PeekPanel
          label={panel.label}
          anchor={anchor}
          container={cardRef.current}
          onClose={close}
          onPointerEnter={keepHover}
          onPointerLeave={(e) => e.pointerType === "mouse" && !pinned && endHover()}
        >
          {panel.body}
        </PeekPanel>
      )}
    </article>
  );
}
